import { z } from 'zod';

import { UuidSchema } from '../common/id/id';

import { PhotoVisibilitySchema } from './photo-visibility/photo-visibility';

/**
 * The member's own profile row — the small set of facts that are **not**
 * questions.
 *
 * Everything a member says about themselves is a question answer (A4); this row
 * holds only what the *platform* needs to reason about them: how far along they
 * are, and how their photos are treated. Keeping it this thin is what stops it
 * becoming the hardcoded profile the question engine exists to replace.
 */
export const MemberProfileSchema = z
  .object({
    userId: UuidSchema,
    /**
     * The one free-text field that is not a question, because it is not a
     * question: it is the member's own paragraph, and it has no label, no type
     * and no visibility policy of its own — it is always shown to a matched
     * viewer and never before.
     */
    about: z.string().nullable(),
    photoVisibility: PhotoVisibilitySchema,
    completionPct: z.number().int().min(0).max(100),
    wizardCompletedAt: z.string().nullable(),
  })
  .strict();

export type MemberProfile = z.infer<typeof MemberProfileSchema>;

const ABOUT_MAX = 2_000;

/** Updating the member's own profile row. */
export const UpdateMemberProfileSchema = z
  .object({
    about: z.string().trim().max(ABOUT_MAX).nullable().optional(),
    photoVisibility: PhotoVisibilitySchema.optional(),
  })
  .strict();

export type UpdateMemberProfileRequest = z.infer<typeof UpdateMemberProfileSchema>;
